import React, { useState, useEffect } from 'react';
import { RefreshCw, Layers, Search, Flag, Activity } from 'lucide-react';
import { apiGet } from '@/services/api';
import { Button, Card, LoadingState, EmptyState } from '@/components/common';
import type { HalCollection } from '@/types';

interface TypeData {
  id: number;
  name: string;
  color: string;
  isMilestone: boolean;
  isDefault: boolean;
  position: number;
}

export function ActivityTypesPage() {
  const [types, setTypes] = useState<TypeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadTypes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiGet<HalCollection<TypeData>>('/auth/data/admin/types');
      setTypes(response._embedded?.elements || []);
    } catch (err) {
      console.error('Failed to load types:', err);
      setError('Eroare la încărcarea tipurilor de activități');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const filteredTypes = types.filter(type => {
    const query = searchQuery.toLowerCase();
    return type.name.toLowerCase().includes(query);
  });

  if (isLoading) {
    return <LoadingState message="Se încarcă tipurile..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-indigo-500 text-white">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tip Activitate</h1>
            <p className="text-gray-600 mt-1">Nomenclator tipuri de activități</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredTypes.length} tipuri
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadTypes}
          >
            Reîncarcă
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută tipuri..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filteredTypes.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="list"
              title="Nu există tipuri"
              description={searchQuery ? "Nu s-au găsit tipuri care să corespundă căutării." : "Nu există tipuri de activități în sistem."}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Poziție
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nume tip
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Culoare
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Categorie
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {type.position}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color || '#6B7280' }}
                        />
                        <span className="font-medium text-gray-900">{type.name}</span>
                        {type.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            Implicit
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{ backgroundColor: type.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-600 font-mono">
                          {type.color || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {type.isMilestone ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          <Flag className="w-3 h-3" />
                          Reper
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <Activity className="w-3 h-3" />
                          Activitate
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
